"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LoginPage = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_i18next_1 = require("react-i18next");
const react_router_dom_1 = require("react-router-dom");
const card_1 = require("../ui/card");
const AuthProvider_1 = require("../../providers/AuthProvider");
const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const LoginPage = () => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const navigate = (0, react_router_dom_1.useNavigate)();
    const { user, login } = (0, AuthProvider_1.useAuth)();
    const buttonRef = (0, react_1.useRef)(null);
    const [clientId, setClientId] = (0, react_1.useState)(null);
    const [error, setError] = (0, react_1.useState)(null);
    // GIS only honors the *first* google.accounts.id.initialize() call per
    // page (logs a warning and silently ignores the rest) — this guards
    // against calling it again if this effect's own deps (clientId) somehow
    // change, without needing navigate/t in its dependency array at all.
    const initializedRef = (0, react_1.useRef)(false);
    const handleCredentialRef = (0, react_1.useRef)(() => { });
    (0, react_1.useEffect)(() => {
        if (user) {
            navigate('/distribution-requests', { replace: true });
        }
    }, [user, navigate]);
    (0, react_1.useEffect)(() => {
        fetch('/api/auth/config')
            .then((response) => response.json())
            .then((config) => setClientId(config.googleClientId))
            .catch(() => setError(t('components.LoginPage.signInError')));
        // Intentionally runs once — this fetches static config, not something
        // that should re-run if `t` happens to get a new reference.
    }, []);
    // Re-registered on every render so the GIS callback (wired up exactly once
    // below) always calls through to the latest navigate/t/setError — without
    // this indirection, keeping those in the initialize effect's own deps is
    // what was re-triggering initialize() on every render in the first place.
    (0, react_1.useEffect)(() => {
        handleCredentialRef.current = async (response) => {
            setError(null);
            const result = await fetch('/api/auth/google', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential: response.credential }),
            });
            if (result.ok) {
                // RequireAuth reads AuthProvider's user state, which only ever comes
                // from the one /api/auth/me fetch on mount — without this, navigating
                // right after login bounces straight back to /login because that
                // state is still null (found via a real sign-in attempt: the cookie
                // was set, but only a manual reload showed the app).
                login(await result.json());
                navigate('/distribution-requests', { replace: true });
            }
            else {
                setError(t('components.LoginPage.signInError'));
            }
        };
    });
    (0, react_1.useEffect)(() => {
        if (!clientId || !buttonRef.current)
            return;
        const renderButton = () => {
            if (!window.google || !buttonRef.current || initializedRef.current)
                return;
            initializedRef.current = true;
            window.google.accounts.id.initialize({
                client_id: clientId,
                callback: (response) => handleCredentialRef.current(response),
            });
            window.google.accounts.id.renderButton(buttonRef.current, { theme: 'outline', size: 'large' });
        };
        if (window.google) {
            renderButton();
            return;
        }
        const script = document.createElement('script');
        script.src = GIS_SCRIPT_SRC;
        script.async = true;
        script.onload = renderButton;
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script);
        };
    }, [clientId]);
    return ((0, jsx_runtime_1.jsx)("div", { className: "flex min-h-screen items-center justify-center bg-background p-4", children: (0, jsx_runtime_1.jsxs)(card_1.Card, { className: "w-full max-w-sm", children: [(0, jsx_runtime_1.jsxs)(card_1.CardHeader, { children: [(0, jsx_runtime_1.jsx)(card_1.CardTitle, { children: t('components.LoginPage.title') }), (0, jsx_runtime_1.jsx)(card_1.CardDescription, { children: t('components.LoginPage.domainNote') })] }), (0, jsx_runtime_1.jsxs)(card_1.CardContent, { className: "flex flex-col items-center gap-3", children: [(0, jsx_runtime_1.jsx)("div", { ref: buttonRef }), error && ((0, jsx_runtime_1.jsx)("p", { role: "alert", className: "text-sm text-destructive", children: error }))] })] }) }));
};
exports.LoginPage = LoginPage;
